import React, { useMemo } from 'react';
import { buttonSizes, buttonVariants } from 'src/tef/components/config/variantConfig';
import { useIcon } from 'src/tef/components/hooks/useIcon';
import { useResponsiveVariant } from 'src/tef/components/hooks/useResponsiveVariant';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import { getBorderRadiusBySize } from 'src/tef/components/utils/getBorderRadius';
import type { ComponentSize, ForwardedHTMLProps } from 'src/tef/components/utils/types';
import type { ComponentVariant, IconProp } from 'src/tef/components/utils/types';

type IconButtonShape = 'square' | 'circle' | 'rounded';

interface IconButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'size'>, ForwardedHTMLProps {
  icon: IconProp;
  variant?: ComponentVariant;
  shape?: IconButtonShape;
  loading?: boolean;
  active?: boolean;
  tooltip?: string;
  className?: string;
}

const shapeClasses: Record<IconButtonShape, string> = {
  square: 'rounded',
  circle: 'rounded-full',
  rounded: 'rounded-lg',
};

const sizePadding: Record<ComponentSize, { square: string; circle: string }> = {
  xs: { square: 'p-1', circle: 'p-1' },
  sm: { square: 'p-1.5', circle: 'p-1.5' },
  md: { square: 'p-2', circle: 'p-2' },
  lg: { square: 'p-2.5', circle: 'p-2.5' },
  xl: { square: 'p-3', circle: 'p-3' },
};

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  (
    {
      icon,
      variant = { style: 'ghost', size: 'md' },
      shape = 'square',
      loading = false,
      active = false,
      disabled,
      tooltip,
      className,
      ...props
    },
    ref
  ) => {
    const tef = useTEF();
    const forwardedProps = forwardProps(props);
    const iconElement = useIcon(icon);

    const variantStyle = variant.style || 'ghost';
    const variantSize = variant.size || 'md';
    const responsiveSize = useResponsiveVariant(variantSize);
    const resolvedSize = typeof responsiveSize === 'string' ? responsiveSize : 'md';
    const sizeConfig = typeof resolvedSize === 'string' ? buttonSizes[resolvedSize] : buttonSizes.md;

    const buttonVariant = buttonVariants[variantStyle as keyof typeof buttonVariants] || buttonVariants.ghost;
    const isDisabled = disabled || loading;
    const borderRadius = getBorderRadiusBySize(resolvedSize, tef);

    const buttonClasses = useMemo(() => {
      const shapeClass = shape === 'rounded' ? borderRadius : shapeClasses[shape];
      const padding = sizePadding[resolvedSize as ComponentSize] || sizePadding.md;
      const paddingClass = shape === 'circle' ? padding.circle : padding.square;

      return cn(
        'inline-flex items-center justify-center',
        'transition-all duration-200',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary',
        'dark:focus-visible:ring-offset-slate-900',
        paddingClass,
        shapeClass,
        buttonVariant.base,
        !isDisabled && buttonVariant.hover,
        !isDisabled && buttonVariant.active,
        isDisabled && buttonVariant.disabled,
        isDisabled && 'cursor-not-allowed',
        active && 'bg-slate-100 dark:bg-slate-700',
        className
      );
    }, [resolvedSize, shape, isDisabled, active, buttonVariant, borderRadius, className]);

    const spinnerSize =
      {
        xs: 'w-3 h-3',
        sm: 'w-3.5 h-3.5',
        md: 'w-4 h-4',
        lg: 'w-5 h-5',
        xl: 'w-6 h-6',
      }[resolvedSize as ComponentSize] || 'w-4 h-4';

    return (
      <button
        ref={ref}
        type={props.type || 'button'}
        disabled={isDisabled}
        className={buttonClasses}
        aria-label={props['aria-label'] || tooltip || 'Icon button'}
        aria-busy={loading}
        aria-pressed={active}
        title={tooltip}
        {...forwardedProps}
        {...props}
      >
        {loading ? (
          <span
            className={cn(spinnerSize, 'border-2 border-current border-t-transparent rounded-full animate-spin')}
            aria-hidden="true"
          />
        ) : (
          <span className={cn('inline-flex items-center justify-center', sizeConfig.icon)} aria-hidden="true">
            {iconElement}
          </span>
        )}
      </button>
    );
  }
);

IconButton.displayName = 'IconButton';

export default IconButton;
